import React from "react";
import { Row, Col, Badge } from "reactstrap";
import { useSelector } from "react-redux";

const CatastroCiudadLimpia = ({ cliente }) => {
  const theme = useSelector((state) => state.theme.theme) || "light";

  const catastrosCiudadLimpia = cliente?.catastros_ciudad_limpia || [];

  // Función para formatear fechas
  const formatDate = (dateString) => {
    if (!dateString) return "Sin datos";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Función para obtener el color del badge según el estado
  const getStatusBadge = (status) => {
    if (status === "S" || status === "Activo") {
      return <Badge color="success" className="px-3 py-2" pill>Activo</Badge>;
    }
    if (status === "N" || status === "Inactivo") {
      return <Badge color="danger" className="px-3 py-2" pill>Inactivo</Badge>;
    }
    return <Badge color="secondary" className="px-3 py-2" pill>{status || "Sin datos"}</Badge>;
  };

  // Componente para mostrar un campo individual
  const InfoField = ({ label, value }) => (
    <div className="vb-field-group">
      <div className="vb-field-label">{label}</div>
      <div className="vb-field-value">{value || <span className="text-muted font-italic">Sin datos</span>}</div>
    </div>
  );

  // Función para renderizar una sección de datos
  const renderSection = (title, icon, children) => (
    <div className="vb-section-card h-100">
      <div className="vb-card-header">
        <i className={icon}></i>
        <span>{title}</span>
      </div>
      <div className="vb-card-body">
        {children}
      </div>
    </div>
  );

  if (!catastrosCiudadLimpia || catastrosCiudadLimpia.length === 0) {
    return (
      <div className="vb-empty-data">
        <i className="mdi mdi-city"></i>
        <h5>No hay datos de Catastro Ciudad Limpia</h5>
        <p>No se encontraron registros asociados a este cliente.</p>
      </div>
    );
  }

  return (
    <div className="vb-scroll-container" data-theme={theme}>
      {catastrosCiudadLimpia.map((catastro, index) => (
        <div key={index} className="mb-5">
          {/* Header con título y estados principales */}
          <div className="vb-section-card mb-4">
            <div className="p-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div>
                <h4 className="mb-2 text-primary" style={{ fontWeight: 800 }}>
                  <i className="mdi mdi-city me-2"></i>
                  Catastro Ciudad Limpia {catastrosCiudadLimpia.length > 1 ? `#${index + 1}` : ""}
                </h4>
                <div className="badge bg-light text-secondary border p-2 mb-3">
                  Cuenta: <strong>{catastro.cuenta_contrato || "Sin datos"}</strong>
                </div>

                <div className="d-flex align-items-center gap-2">
                  <i className="mdi mdi-map-marker text-danger"></i>
                  <span className="fw-bold text-dark">{catastro.direccion || "Sin dirección registrada"}</span>
                </div>
              </div>
              <div className="text-end">
                <div className="mb-2">{getStatusBadge(catastro.estado)}</div>
                <div className="text-muted small mb-1">Estado Cobro</div>
                {getStatusBadge(catastro.estado_cobro)}
              </div>
            </div>
          </div>

          <Row className="g-4">
            {/* Información General y Ubicación */}
            <Col lg={6}>
              {renderSection(
                "Información General",
                "mdi mdi-information-outline",
                <Row>
                  <Col sm={6}>
                    <InfoField label="Suscriptor" value={catastro.suscriptor} />
                    <InfoField label="Tipo Población" value={catastro.tipo_poblacion} />
                  </Col>
                  <Col sm={6}>
                    <InfoField label="Descripción Tipo Productor" value={catastro.descripcion_tipoproductor} />
                  </Col>
                </Row>
              )}
            </Col>

            <Col lg={6}>
              {renderSection(
                "Ubicación Detallada",
                "mdi mdi-map-marker-radius-outline",
                <>
                  <Row>
                    <Col sm={6}>
                      <InfoField label="Estrato" value={catastro.estrato} />
                      <InfoField label="Manzana" value={catastro.manzana} />
                    </Col>
                    <Col sm={6}>
                      <InfoField label="Lote" value={catastro.lote} />
                      <InfoField label="Código Barrio" value={catastro.codigo_barrio} />
                    </Col>
                  </Row>
                  {(catastro.coordenadax || catastro.coordenaday) && (
                    <Row className="mt-2">
                      <Col sm={12}><hr className="my-2 opacity-25" /></Col>
                      <Col sm={6}>
                        <InfoField label="Coordenada X" value={catastro.coordenadax} />
                      </Col>
                      <Col sm={6}>
                        <InfoField label="Coordenada Y" value={catastro.coordenaday} />
                      </Col>
                    </Row>
                  )}
                </>
              )}
            </Col>

            {/* Servicios y Otros Datos */}
            <Col lg={6}>
              {renderSection(
                "Servicios y Ciclos",
                "mdi mdi-truck-fast-outline",
                <Row>
                  <Col sm={6}>
                    <InfoField label="Ciclo" value={catastro.ciclo} />
                    <InfoField label="ASE" value={catastro.ase} />
                    <InfoField label="Barrido" value={catastro.barrido} />
                  </Col>
                  <Col sm={6}>
                    <InfoField label="Recolección" value={catastro.recoleccion} />
                    <InfoField label="Microbarrido" value={catastro.microbarrido} />
                    <InfoField label="Microrecolección" value={catastro.microrecoleccion} />
                  </Col>
                </Row>
              )}
            </Col>

            <Col lg={6}>
              {renderSection(
                "Facturación y Datos Adicionales",
                "mdi mdi-file-document-outline",
                <>
                  <Row>
                    <Col sm={6}>
                      <InfoField label="Tipo Facturación" value={catastro.tipo_facturacion} />
                      <InfoField label="Chip" value={catastro.chip} />
                    </Col>
                    <Col sm={6}>
                      <InfoField label="Cuenta Fac. Conjunta" value={catastro.cuenta_fac_conjunta} />
                      <InfoField label="Volumen" value={catastro.volumen} />
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col sm={6}>
                      <div className="d-flex align-items-center gap-2">
                        <i className="mdi mdi-door-open text-primary"></i>
                        <InfoField
                          label="Puerta a Puerta"
                          value={catastro.puertaapuerta === "S" ? "Sí" : catastro.puertaapuerta || "No"}
                        />
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </Col>

            {/* Fechas */}
            {(catastro.fecha_ingreso || catastro.fecha_aforo) && (
              <Col lg={12}>
                {renderSection(
                  "Fechas Importantes",
                  "mdi mdi-calendar-clock",
                  <Row>
                    <Col sm={6}>
                      <InfoField label="Fecha de Ingreso" value={formatDate(catastro.fecha_ingreso)} />
                    </Col>
                    <Col sm={6}>
                      <InfoField label="Fecha de Aforo" value={formatDate(catastro.fecha_aforo)} />
                    </Col>
                  </Row>
                )}
              </Col>
            )}
          </Row>
        </div>
      ))}
    </div>
  );
};

export default CatastroCiudadLimpia;